import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { formatAddress } from '../utils/formatters';
import * as SubframeCore from '@subframe/core';
import { Button } from '../ui/components/Button';
import { IconWithBackground } from '../ui/components/IconWithBackground';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Sparkline } from '../components/home/Sparkline';
import { useHomePageData } from '../hooks/useHomePageData';
import { useDashboard } from '../hooks/useDashboard';
import { useAuthStore } from '../store/authStore';
import { FeatherImage } from '@subframe/core';
import type { LeaderboardCollection, TrendingCollection, FloorPriceGainerCollection, PromotedNft } from '../api/analytics';
import type {
  DashboardData,
  DashboardExpiringOffer,
  DashboardListing,
  DashboardMyCollection,
  DashboardSinceVisit,
  DashboardSinceVisitSignal,
  DashboardTopValuedNft,
} from '../api/dashboard';

// ─── Formatting helpers ──────────────────────────────────────────
const fmtXRP = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
};

// Compact price formatter with up to 2 decimals, trailing zeros trimmed.
// Used for small-text listing prices where fmtXRP's integer rounding would
// make sub-1 XRP prices look like "0 XRP".
const fmtPrice = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  if (n >= 100) return n.toFixed(0);
  // Show up to 2 decimals for values < 100, trim trailing zeros.
  return n.toFixed(2).replace(/\.?0+$/, '');
};

const fmtUSD = (n: number | null): string => {
  if (n === null) return '';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtExpires = (mins: number): string => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return `${d}d ${hh}h`;
};

// ─── Truncated address with click-to-copy ────────────────────────
const AddressWithCopy: React.FC<{ address: string }> = ({ address }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — some browsers block clipboard without focus
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-mono text-neutral-400 tracking-wider">
      {/* Full address on desktop; truncated on mobile so it doesn't wrap. */}
      <span className="mobile:hidden">/ {address}</span>
      <span className="hidden mobile:inline">/ {formatAddress(address)}</span>
      <button
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy address'}
        className="p-0.5 rounded hover:bg-neutral-50 transition-colors"
      >
        {copied ? (
          <SubframeCore.FeatherCheck className="w-3.5 h-3.5 text-success-800" />
        ) : (
          <SubframeCore.FeatherCopy className="w-3.5 h-3.5 text-neutral-400 hover:text-default-font" />
        )}
      </button>
    </span>
  );
};

// Gap-percentage color: small negative = green (healthy offer), moderate
// negative = yellow (watchlist), wider = red (far off). The value passed in
// is the positive gap magnitude (e.g., a 12% gap comes in as 12, not -12).
const gapToneClass = (gapMagnitude: number): string => {
  if (gapMagnitude < 10) return 'text-success-800';
  if (gapMagnitude < 25) return 'text-warning-800';
  return 'text-error-800';
};

// ─── Delta chip (↑ 4.2% colored) ─────────────────────────────────
const Delta: React.FC<{ value: number; size?: 'xs' | 'sm' }> = ({ value, size = 'xs' }) => {
  const sizeClass = size === 'sm' ? 'text-sm' : 'text-xs';
  // Exact-zero is shown as a neutral dash so we don't mislead with a green
  // up-arrow on an unchanged value.
  if (value === 0) {
    return (
      <span className={`font-mono font-medium ${sizeClass} text-neutral-400`}>
        − 0.0%
      </span>
    );
  }
  const up = value > 0;
  const colorClass = up ? 'text-success-800' : 'text-error-800';
  return (
    <span className={`font-mono font-medium ${sizeClass} ${colorClass}`}>
      {up ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
    </span>
  );
};

// ─── Section label ("header" for each card) ──────────────────────
const SectionLabel: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({
  children,
  right,
}) => (
  <div className="flex items-center justify-between border-b border-neutral-border pb-2">
    <div className="flex items-center gap-2">
      <span className="w-1 h-3 bg-brand-primary" />
      <h3 className="text-xs font-mono font-semibold uppercase tracking-[0.12em] text-neutral-700">
        {children}
      </h3>
    </div>
    {right}
  </div>
);

// ─── Card wrapper ────────────────────────────────────────────────
const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`flex flex-col gap-3 border border-neutral-border bg-default-background p-4 ${className}`}
  >
    {children}
  </div>
);

// ─── Collection thumbnail (44px default) ───────────────────────
const Thumb: React.FC<{ src?: string | null; size?: number }> = ({ src, size = 44 }) => (
  <div
    className="flex-none overflow-hidden bg-neutral-50 border border-neutral-border"
    style={{ width: size, height: size }}
  >
    {src ? (
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : (
      <IconWithBackground size="small" icon={<FeatherImage />} />
    )}
  </div>
);

// ─── Featured strip ─────────────────────────────────────────────
const FeaturedStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] font-mono uppercase tracking-wider text-subtext-color">
      {label}
    </span>
    <span className="text-sm font-mono font-semibold text-default-font">{value}</span>
  </div>
);

const FeaturedStrip: React.FC<{
  featured: any;
  onClick: () => void;
  onNftClick: (tokenId: string) => void;
}> = ({ featured, onClick, onNftClick }) => {
  if (!featured) return null;
  const previews: Array<{
    nft_token_id: string;
    image_url: string | null;
    price: number | null;
    rarity_rank: number | null;
  }> = featured.previewNfts || [];
  const previewMode: 'rarity' | 'cheapest' = featured.previewMode || 'cheapest';
  const previewLabel = previewMode === 'rarity' ? 'Top ranked listings' : 'Cheapest listings';

  // Over-fetch 8 so the lg 2×4 grid has enough. xl full-layout slices to 6.
  const shownPreviews = previews.slice(0, 8);

  return (
    <div className="w-full flex items-stretch border border-brand-primary bg-brand-50 overflow-hidden">
      {/* Cover image: desktop/tablet only. Hidden on mobile to keep the strip compact.
          Wrapper centers the image vertically within the strip (which is taller than
          the image itself once stats wrap) and adds left padding so it's not flush. */}
      <div className="flex-none flex items-center pl-4 mobile:hidden">
        <div className="w-24 h-24 bg-brand-400 overflow-hidden">
          {featured.imageUrl && (
            <img src={featured.imageUrl} alt="" className="w-full h-full object-cover" />
          )}
        </div>
      </div>

      {/* Info block. On mobile only the FEATURED label + name render — description
          and stats are hidden to preserve a single compact row. */}
      <div className="p-4 flex flex-col gap-1.5 justify-center min-w-0 flex-1 mobile:flex-1 mobile:gap-1">
        <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-brand-800">
          FEATURED
        </span>
        <h2 className="text-lg font-mono font-semibold text-default-font truncate mobile:text-base">
          {featured.name}
        </h2>
        {featured.description && (
          <div className="text-xs font-mono text-subtext-color line-clamp-1 mobile:hidden">
            {featured.description}
          </div>
        )}
        <div className="flex items-center gap-5 mt-0.5 flex-wrap mobile:hidden">
          <FeaturedStat
            label="Floor"
            value={featured.floorPrice ? `${fmtXRP(Number(featured.floorPrice))} XRP` : '—'}
          />
          <FeaturedStat
            label="Vol 24h"
            value={`${fmtXRP(Number(featured.volume24h || 0))} XRP`}
          />
          <FeaturedStat
            label="Vol all-time"
            value={`${fmtXRP(Number(featured.volumeAllTime || 0))} XRP`}
          />
          {featured.uniqueHolders != null && (
            <FeaturedStat label="Holders" value={featured.uniqueHolders.toLocaleString()} />
          )}
          {featured.totalSupply != null && (
            <FeaturedStat label="Supply" value={featured.totalSupply.toLocaleString()} />
          )}
        </div>
      </div>

      {/* Compact preview: lg to <xl. Pure 2x4 image grid — no header, no
          price, no rank — because those labels squish into illegibility at
          this width. Clicking a thumb still navigates. */}
      {shownPreviews.length > 0 && (
        <div className="hidden lg:flex xl:hidden lg:flex-1 lg:min-w-0 lg:flex-col lg:justify-center lg:px-3 lg:py-2 lg:border-l lg:border-brand-primary/40">
          <div className="grid grid-cols-4 grid-rows-2 gap-1">
            {shownPreviews.slice(0, 8).map(p => (
              <div
                key={p.nft_token_id}
                onClick={() => onNftClick(p.nft_token_id)}
                className="aspect-square bg-neutral-50 border border-neutral-border overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                title={p.price !== null ? `${fmtPrice(p.price)} XRP` : ''}
              >
                {p.image_url && (
                  <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full preview: xl+. Header + 1x6 with price and (optionally) rank. */}
      {shownPreviews.length > 0 && (
        <div className="hidden xl:flex xl:flex-1 xl:min-w-0 xl:flex-col xl:justify-center xl:px-3 xl:border-l xl:border-brand-primary/40">
          <div className="text-[10px] font-mono uppercase tracking-wider text-subtext-color mb-1">
            {previewLabel}
          </div>
          <div className="grid grid-cols-6 gap-2">
            {shownPreviews.slice(0, 6).map(p => (
              <div
                key={p.nft_token_id}
                onClick={() => onNftClick(p.nft_token_id)}
                className="flex flex-col gap-0.5 cursor-pointer hover:opacity-80 transition-opacity min-w-0"
                title={p.price !== null ? `${fmtPrice(p.price)} XRP` : ''}
              >
                <div className="aspect-square bg-neutral-50 border border-neutral-border overflow-hidden">
                  {p.image_url && (
                    <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                {p.price !== null && (
                  <div className="text-[10px] font-mono font-semibold text-brand-primary truncate text-center">
                    {fmtPrice(p.price)} XRP
                  </div>
                )}
                {previewMode === 'rarity' && p.rarity_rank != null && (
                  <div className="text-[9px] font-mono text-subtext-color truncate text-center">
                    #{p.rarity_rank}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center p-4 border-l border-brand-primary mobile:p-3">
        <Button size="small" onClick={onClick}>
          View collection
        </Button>
      </div>
    </div>
  );
};

// ─── Featured NFT (paid promotion) ──────────────────────────────
// Prefer the CDN thumbnail over `image_url` because the latter is often an
// `ipfs://` URI which browsers can't render directly. Only fall back to
// `imageUrl` if it's a real HTTP(S) URL.
const resolveNftImage = (p: PromotedNft): string | null => {
  if (p.thumbnailUrl) return p.thumbnailUrl;
  if (p.imageUrl && /^https?:\/\//i.test(p.imageUrl)) return p.imageUrl;
  return null;
};

const FeaturedNftCard: React.FC<{
  promotion: PromotedNft;
  onNftClick: (tokenId: string) => void;
  onCollectionClick: (id: string, slug?: string) => void;
}> = ({ promotion, onNftClick, onCollectionClick }) => {
  const img = resolveNftImage(promotion);
  const isListed = promotion.minSellPrice != null && promotion.minSellPrice > 0;
  return (
    // Brand-yellow surround mirrors the FeaturedStrip's promoted-content
    // styling so the paid spot is visually consistent across the dashboard.
    // `self-start` keeps the card at its content height when the sibling
    // Top Volume table expands ("Show more") — without it the grid row
    // stretches both columns equally and the square image grows oddly.
    <div className="flex flex-col gap-3 border border-brand-primary bg-brand-50 p-4 self-start">
      <SectionLabel>Featured NFT</SectionLabel>
      <div
        onClick={() => onNftClick(promotion.nftTokenId)}
        className="aspect-square w-full bg-neutral-50 border border-brand-primary overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
      >
        {img ? (
          <img src={img} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <IconWithBackground size="medium" icon={<FeatherImage />} />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 min-w-0">
        {promotion.collectionName && promotion.collectionId && (
          <button
            type="button"
            onClick={() =>
              onCollectionClick(promotion.collectionId!, promotion.collectionSlug ?? undefined)
            }
            className="text-[10px] font-mono uppercase tracking-wider text-subtext-color hover:text-brand-primary transition-colors text-left truncate"
          >
            {promotion.collectionName}
          </button>
        )}
        <button
          type="button"
          onClick={() => onNftClick(promotion.nftTokenId)}
          className="text-sm font-mono font-semibold text-default-font hover:text-brand-primary transition-colors text-left truncate"
        >
          {promotion.nftName || `#${promotion.nftTokenId.slice(-6)}`}
        </button>
        <div className="flex flex-col gap-0.5">
          {promotion.rarityRank != null && (
            <div className="flex items-center gap-1.5 text-[11px] font-mono">
              <span className="text-subtext-color uppercase tracking-wider text-[10px]">Rank</span>
              <span className="text-default-font">
                #{promotion.rarityRank}
                {promotion.collectionTotalSupply != null && promotion.collectionTotalSupply > 0 && (
                  <span className="text-subtext-color">/{promotion.collectionTotalSupply.toLocaleString()}</span>
                )}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-[11px] font-mono">
            <span className="text-subtext-color uppercase tracking-wider text-[10px]">
              {isListed ? 'Listed for' : 'Not listed'}
            </span>
            {isListed && (
              <span className="font-semibold text-brand-primary">
                {fmtPrice(promotion.minSellPrice!)} XRP
              </span>
            )}
          </div>
          {promotion.ownerAddress && (
            <div className="flex items-center gap-1.5 text-[11px] font-mono">
              <span className="text-subtext-color uppercase tracking-wider text-[10px]">Owner</span>
              <Link
                to={`/wallets/${promotion.ownerAddress}`}
                className="text-default-font hover:text-brand-primary transition-colors truncate"
              >
                {formatAddress(promotion.ownerAddress)}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Market stats (shared: Top Volume + Trending + Rising) ──────
interface MarketStatsProps {
  topVolume: LeaderboardCollection[];
  trending: TrendingCollection[];
  topGainers: FloorPriceGainerCollection[];
  activePromotions: PromotedNft[];
  onCollectionClick: (id: string, slug?: string) => void;
  onNftClick: (tokenId: string) => void;
}

const MarketStats: React.FC<MarketStatsProps> = ({
  topVolume,
  trending,
  topGainers,
  activePromotions,
  onCollectionClick,
  onNftClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  // Defensive: any of these arrays could be undefined if the home-page
  // endpoint hiccups or returns a partial shape. Don't assume they exist.
  const safeTopVolume = topVolume ?? [];
  const safeTrending = trending ?? [];
  const safeTopGainers = topGainers ?? [];
  const safePromotions = activePromotions ?? [];
  const visibleTopVolume = safeTopVolume.slice(0, expanded ? 10 : 5);
  const canExpand = safeTopVolume.length > 5;
  // Pick a random active promotion once per page load. Re-picks only when
  // the underlying list changes (e.g. after a refetch returns new entries).
  const featuredPromotion = React.useMemo(() => {
    if (safePromotions.length === 0) return null;
    return safePromotions[Math.floor(Math.random() * safePromotions.length)];
  }, [safePromotions]);
  return (
  <div className="flex flex-col gap-4">
    {/* Top Volume + Featured NFT — Featured sits to the right on xl+ where
        the Top Volume table can spare ~320px; stacks below on narrower
        screens so the table keeps its column widths. */}
    <div
      className={
        featuredPromotion
          ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4'
          : 'flex flex-col gap-4'
      }
    >
    <Card>
      <SectionLabel>Top Volume · 24h</SectionLabel>
      <div className="grid grid-cols-[20px_44px_1fr_140px_90px_70px_70px_80px_90px] gap-3 px-2 text-[10px] font-mono uppercase tracking-wider text-neutral-400 pb-1 border-b border-neutral-100 mobile:grid-cols-[20px_44px_1fr_110px]">
        <span />
        <span />
        <span>Collection</span>
        <span className="text-right">
          <span className="mobile:hidden">Volume · vs 30d avg</span>
          <span className="hidden mobile:inline">Vol · vs 30d</span>
        </span>
        <span className="text-right mobile:hidden">30D vol</span>
        <span className="text-right mobile:hidden">Sales</span>
        <span className="text-right mobile:hidden">Holders</span>
        <span className="text-right mobile:hidden">Floor</span>
        <span className="text-right mobile:hidden">30D floor</span>
      </div>
      {visibleTopVolume.map((c, i) => (
        <div
          key={c.collectionId}
          onClick={() => onCollectionClick(c.collectionId, c.slug)}
          className="grid grid-cols-[20px_44px_1fr_140px_90px_70px_70px_80px_90px] gap-3 items-center py-2 px-2 -mx-2 cursor-pointer hover:bg-neutral-50 transition-colors mobile:grid-cols-[20px_44px_1fr_110px]"
        >
          <span className="text-xs font-mono text-neutral-400">{i + 1}</span>
          <Thumb src={c.imageUrl} />
          <div className="min-w-0">
            <div className="text-sm font-mono font-medium text-default-font truncate">
              {c.name}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-mono font-semibold text-brand-primary">
              {fmtXRP(Number(c.volume24h))} XRP
            </div>
            <Delta value={c.volumeChange ?? 0} />
          </div>
          <div className="mobile:hidden flex justify-end">
            {c.volume30d && c.volume30d.length > 0 ? (
              <Sparkline
                data={c.volume30d}
                width={80}
                height={28}
                color="rgb(99,143,247)"
              />
            ) : (
              <span className="text-[11px] font-mono text-neutral-400 self-center">—</span>
            )}
          </div>
          <span className="text-right text-sm font-mono text-neutral-700 mobile:hidden">
            {c.salesCount ?? '—'}
          </span>
          <span className="text-right text-sm font-mono text-neutral-700 mobile:hidden">
            {c.ownersCount ? c.ownersCount.toLocaleString() : '—'}
          </span>
          <span className="text-right text-sm font-mono text-neutral-700 mobile:hidden">
            {c.floorPrice ? `${fmtXRP(Number(c.floorPrice))} XRP` : '—'}
          </span>
          <div className="mobile:hidden flex justify-end">
            <Sparkline
              data={c.floor30d}
              width={80}
              height={28}
              color={c.floorPriceChange >= 0 ? 'rgb(76,195,138)' : 'rgb(246,92,182)'}
            />
          </div>
        </div>
      ))}
      {canExpand && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="mt-1 text-[11px] font-mono uppercase tracking-wider text-subtext-color hover:text-brand-primary transition-colors self-center px-2"
        >
          {expanded ? '− Show less' : '+ Show more'}
        </button>
      )}
    </Card>
    {featuredPromotion && (
      <FeaturedNftCard
        promotion={featuredPromotion}
        onNftClick={onNftClick}
        onCollectionClick={onCollectionClick}
      />
    )}
    </div>

    {/* Trending + Rising Floors — two cols */}
    <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1">
      <Card>
        <SectionLabel>Trending · 24h</SectionLabel>
        <div className="flex items-center justify-between px-2 text-[10px] font-mono uppercase tracking-wider text-neutral-400 pb-1 border-b border-neutral-100">
          <span>Collection</span>
          <span className="text-right pr-[6px]">30D sales</span>
        </div>
        {safeTrending.slice(0, 5).map((c, i) => (
          <div
            key={c.collectionId}
            onClick={() => onCollectionClick(c.collectionId, c.slug)}
            className="grid grid-cols-[20px_44px_1fr_80px] gap-3 items-center py-2 px-2 -mx-2 cursor-pointer hover:bg-neutral-50 transition-colors"
          >
            <span className="text-xs font-mono text-neutral-400">{i + 1}</span>
            <Thumb src={c.imageUrl} />
            <div className="min-w-0">
              <div className="text-sm font-mono font-medium text-default-font truncate">
                {c.name}
              </div>
              {/* Desktop: inline bullet-separated. Mobile: stacked. */}
              <div className="text-[11px] font-mono text-subtext-color mobile:hidden">
                {c.currentSales} {c.currentSales === 1 ? 'sale' : 'sales'}
                {c.uniqueBuyers > 0 && ` · ${c.uniqueBuyers} ${c.uniqueBuyers === 1 ? 'buyer' : 'buyers'}`}
                {' '}· {fmtXRP(Number(c.currentVolume))} XRP
              </div>
              <div className="hidden mobile:flex mobile:flex-col text-[11px] font-mono text-subtext-color leading-tight">
                <span>{c.currentSales} {c.currentSales === 1 ? 'sale' : 'sales'}</span>
                <span>
                  {c.uniqueBuyers > 0 &&
                    `${c.uniqueBuyers} ${c.uniqueBuyers === 1 ? 'buyer' : 'buyers'} · `}
                  {fmtXRP(Number(c.currentVolume))} XRP
                </span>
              </div>
            </div>
            <div className="flex justify-end">
              {c.sales30d && c.sales30d.length > 0 && (
                <Sparkline
                  data={c.sales30d}
                  width={80}
                  height={28}
                  color="rgb(99,143,247)"
                />
              )}
            </div>
          </div>
        ))}
      </Card>

      <Card>
        <SectionLabel>Rising Floors · vs 30d avg</SectionLabel>
        <div className="flex items-center justify-between px-2 text-[10px] font-mono uppercase tracking-wider text-neutral-400 pb-1 border-b border-neutral-100">
          <span>Collection</span>
          {/* pad-right approximates the combined width of the "↑ X%" column
              so the "30D floor" label sits over the sparkline */}
          <span className="text-right pr-[90px] mobile:hidden">30D floor</span>
        </div>
        {safeTopGainers.slice(0, 5).map((c, i) => (
          <div
            key={c.collectionId}
            onClick={() => onCollectionClick(c.collectionId, c.slug)}
            className="grid grid-cols-[20px_44px_1fr_80px_auto] gap-3 items-center py-2 px-2 -mx-2 cursor-pointer hover:bg-neutral-50 transition-colors mobile:grid-cols-[20px_44px_1fr_auto]"
          >
            <span className="text-xs font-mono text-neutral-400">{i + 1}</span>
            <Thumb src={c.imageUrl} />
            <div className="min-w-0">
              <div className="text-sm font-mono font-medium text-default-font truncate">
                {c.name}
              </div>
              <div className="text-[11px] font-mono text-subtext-color">
                floor {c.currentFloorPrice ? fmtXRP(c.currentFloorPrice) : '—'} XRP
              </div>
            </div>
            {/* Sparkline: desktop only. Below 768px the chart compresses to
                illegibility, and the adjacent % is the real signal. */}
            <div className="flex justify-end mobile:hidden">
              {c.floor30d && c.floor30d.length > 0 ? (
                <Sparkline
                  data={c.floor30d}
                  width={80}
                  height={28}
                  color="rgb(76,195,138)"
                />
              ) : null}
            </div>
            <div className="text-sm font-mono font-semibold text-success-800">
              ↑ {c.floorPriceChange.toFixed(1)}%
            </div>
          </div>
        ))}
      </Card>
    </div>
  </div>
  );
};

// ─── Logged-out Market Home ─────────────────────────────────────
const MarketHome: React.FC<{
  topVolume: LeaderboardCollection[];
  trending: TrendingCollection[];
  topGainers: FloorPriceGainerCollection[];
  featured: any;
  activePromotions: PromotedNft[];
  onCollectionClick: (id: string, slug?: string) => void;
  onNftClick: (tokenId: string) => void;
}> = ({ topVolume, trending, topGainers, featured, activePromotions, onCollectionClick, onNftClick }) => (
  <div className="w-full flex flex-col gap-6">
    {/* The persistent global "connect wallet" banner in DefaultPageLayout
        already serves as the logged-out CTA — no dashboard-specific card
        needed. Go straight to the market content. */}
    {featured && (
      <FeaturedStrip
        featured={featured}
        onClick={() => onCollectionClick(featured.collectionId, featured.slug)}
        onNftClick={onNftClick}
      />
    )}

    <MarketStats
      topVolume={topVolume}
      trending={trending}
      topGainers={topGainers}
      activePromotions={activePromotions}
      onCollectionClick={onCollectionClick}
      onNftClick={onNftClick}
    />
  </div>
);

// ─── Portfolio summary cell ─────────────────────────────────────
const StatCell: React.FC<{
  label: string;
  value: string | number;
  unit?: string;
  hint?: string;
  hintWarn?: boolean;
}> = ({ label, value, unit, hint, hintWarn }) => (
  <div className="p-5 border-r border-neutral-border last:border-r-0 flex flex-col gap-1 mobile:p-3 mobile:border-r-0 mobile:border-b mobile:last:border-b-0">
    <span className="text-[10px] font-mono uppercase tracking-wider text-subtext-color">
      {label}
    </span>
    <div className="flex items-baseline gap-1.5">
      <span className="text-[30px] leading-9 font-mono font-semibold text-default-font mobile:text-2xl mobile:leading-7">
        {value}
      </span>
      {unit && <span className="text-sm font-mono text-subtext-color">{unit}</span>}
    </div>
    {hint && (
      <span
        className={`text-[11px] font-mono ${hintWarn ? 'text-warning-800' : 'text-subtext-color'}`}
      >
        {hint}
      </span>
    )}
  </div>
);

// ─── Expiring offers card ───────────────────────────────────────
const ExpiringOffersCard: React.FC<{
  offers: DashboardExpiringOffer[];
  totalBuyOffers: number;
  onNftClick: (tokenId: string) => void;
  onViewAll: () => void;
}> = ({ offers, totalBuyOffers, onNftClick, onViewAll }) => {
  const expireTone = (mins: number) => {
    if (mins < 360) return 'text-warning-800';
    if (mins < 1440) return 'text-neutral-700';
    return 'text-subtext-color';
  };
  return (
    <Card>
      <SectionLabel
        right={
          <Button size="small" variant="brand-tertiary" onClick={onViewAll}>
            View all ({totalBuyOffers})
          </Button>
        }
      >
        Offers expiring soonest
      </SectionLabel>
      {offers.map((o, i) => (
        <div
          key={o.id}
          onClick={() => onNftClick(o.tokenId)}
          className={`grid grid-cols-[44px_1fr_auto_auto] gap-3 items-center py-2.5 px-2 -mx-2 cursor-pointer hover:bg-neutral-50 ${
            i > 0 ? 'border-t border-neutral-100' : ''
          }`}
        >
          <Thumb src={o.image} />
          <div className="min-w-0">
            <div className="text-sm font-mono font-medium text-default-font truncate">{o.nft}</div>
            <div className="text-[11px] font-mono text-subtext-color truncate">{o.collection}</div>
          </div>
          <div className="text-right pr-3">
            <div className="text-sm font-mono font-semibold text-brand-primary">
              {o.offerPrice} XRP
            </div>
            <div className="text-[11px] font-mono text-subtext-color">floor {o.floor}</div>
          </div>
          <div className="text-right">
            <div className={`text-sm font-mono font-medium ${expireTone(o.expiresMins)}`}>
              {fmtExpires(o.expiresMins)}
            </div>
            <div className="text-[10px] font-mono uppercase text-neutral-400">expires</div>
          </div>
        </div>
      ))}
    </Card>
  );
};

// ─── Top Valued NFTs ────────────────────────────────────────────
// Your most valuable owned NFTs by `estimated_value`, liquidity-filtered
// to match the portfolio-value gate. The `basis` label hints at where the
// number came from: offer = active buy offer, floor = collection floor,
// valuation = model-computed (e.g., rarity-adjusted).
const TopValuedNftsCard: React.FC<{
  nfts: DashboardTopValuedNft[];
  onNftClick: (tokenId: string) => void;
}> = ({ nfts, onNftClick }) => {
  const basisTone = (b: DashboardTopValuedNft['basis']) =>
    b === 'offer' ? 'text-success-800' : 'text-neutral-400';
  const basisLabel = (b: DashboardTopValuedNft['basis']) =>
    b === 'offer' ? 'offer' : b === 'floor' ? 'floor' : 'Est. Value';
  return (
    <Card>
      <SectionLabel>Top Valued NFTs</SectionLabel>
      {nfts.length === 0 ? (
        <div className="text-sm font-mono text-subtext-color py-4 text-center">
          No liquid NFT valuations available yet.
        </div>
      ) : (
        nfts.map((n, i) => (
          <div
            key={n.tokenId}
            onClick={() => onNftClick(n.tokenId)}
            className={`grid grid-cols-[44px_1fr_90px_80px] gap-3 items-center py-2.5 px-2 -mx-2 cursor-pointer hover:bg-neutral-50 ${
              i > 0 ? 'border-t border-neutral-100' : ''
            }`}
          >
            <Thumb src={n.image} />
            <div className="min-w-0">
              <div className="text-sm font-mono font-medium text-default-font truncate">
                {n.name}
              </div>
              <div className="text-[11px] font-mono text-subtext-color truncate">
                {n.collection}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-mono font-semibold text-brand-primary">
                {fmtPrice(n.value)} XRP
              </div>
              <div className={`text-[10px] font-mono uppercase ${basisTone(n.basis)}`}>
                {basisLabel(n.basis)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm leading-tight">
                {typeof n.roi === 'number' ? (
                  <Delta value={n.roi} size="sm" />
                ) : (
                  <span className="font-mono font-medium text-sm text-neutral-400">—</span>
                )}
              </div>
              <div className="text-[10px] font-mono uppercase text-neutral-400">ROI</div>
            </div>
          </div>
        ))
      )}
    </Card>
  );
};

// ─── My Collections ─────────────────────────────────────────────
const MyCollectionsCard: React.FC<{
  collections: DashboardMyCollection[];
  onCollectionClick: (id: string, slug?: string) => void;
  onViewAll: () => void;
}> = ({ collections, onCollectionClick, onViewAll }) => (
  <Card>
    <SectionLabel
      right={
        <Button size="small" variant="brand-tertiary" onClick={onViewAll}>
          All NFTs
        </Button>
      }
    >
      My Collections<span className="mobile:hidden"> · top by value</span>
    </SectionLabel>
    {collections.length === 0 ? (
      <div className="text-sm font-mono text-subtext-color py-4 text-center">
        You don't own any NFTs in indexed collections yet.
      </div>
    ) : (
      collections.slice(0, 5).map((c, i) => (
        <div
          key={c.id}
          onClick={() => onCollectionClick(c.id, c.slug || undefined)}
          className={`grid grid-cols-[44px_1fr_100px_80px] gap-3 items-center py-2.5 px-2 -mx-2 cursor-pointer hover:bg-neutral-50 ${
            i > 0 ? 'border-t border-neutral-100' : ''
          }`}
        >
          <Thumb src={c.image} />
          <div className="min-w-0">
            <div className="text-sm font-mono font-medium text-default-font truncate">{c.name}</div>
            {/* Desktop: inline bullet-separated. Mobile: stacked to avoid
                awkward wrapping in the narrow column. */}
            <div className="text-[11px] font-mono text-subtext-color mobile:hidden">
              <span className="text-neutral-700">{c.count}</span> owned · floor {c.floor} XRP
              {c.floor > 0 && c.value === 0 && (
                <span className="ml-1 text-neutral-400">· illiquid</span>
              )}
            </div>
            <div className="hidden mobile:flex mobile:flex-col text-[11px] font-mono text-subtext-color leading-tight">
              <span>
                <span className="text-neutral-700">{c.count}</span> owned
              </span>
              <span>floor {c.floor} XRP</span>
              {c.floor > 0 && c.value === 0 && (
                <span className="text-neutral-400">illiquid</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-mono font-semibold text-brand-primary">
              {c.value.toLocaleString()} XRP
            </div>
            <div className="text-[10px] font-mono uppercase text-neutral-400">value</div>
          </div>
          <div className="text-right">
            <div className="text-sm leading-tight">
              <Delta value={c.change24h_pct} size="sm" />
            </div>
            <div className="text-[10px] font-mono uppercase text-neutral-400">floor 24h</div>
          </div>
        </div>
      ))
    )}
  </Card>
);

// ─── My Listings ────────────────────────────────────────────────
// ─── Buy offers on your NFTs ────────────────────────────────────
// Full-width rich table showing every owned NFT with at least one funded
// buy offer — listed or not. Columns mirror the old My Listings view so
// sellers can see list price, floor, best offer, gap, views, expires all
// at a glance.
const BuyOffersReceivedCard: React.FC<{
  rows: DashboardListing[];
  onNftClick: (tokenId: string) => void;
  onViewAll: () => void;
}> = ({ rows, onNftClick, onViewAll }) => (
  <Card>
    <SectionLabel
      right={
        <Button size="small" variant="brand-tertiary" onClick={onViewAll}>
          All offers
        </Button>
      }
    >
      Buy offers on your NFTs
    </SectionLabel>
    {rows.length === 0 ? (
      <div className="text-sm font-mono text-subtext-color py-4 text-center">
        No active buy offers on your NFTs yet.
      </div>
    ) : (
      <>
        <div className="grid grid-cols-[40px_1fr_110px_100px_90px_70px_90px_80px] gap-4 items-center px-2 text-[10px] font-mono uppercase tracking-wider text-neutral-400 border-b border-neutral-100 pb-2 mobile:hidden">
          <span />
          <span>NFT</span>
          <span className="text-right">List price</span>
          <span className="text-right">Floor</span>
          <span className="text-right">Best offer</span>
          <span className="text-right">Gap</span>
          <span className="text-right">Views</span>
          <span className="text-right">Expires</span>
        </div>
        {rows.slice(0, 5).map(l => {
          const gap =
            l.highestOffer !== null && l.listPrice !== null && l.listPrice > 0
              ? ((l.listPrice - l.highestOffer) / l.listPrice) * 100
              : null;
          return (
            <div
              key={l.id}
              onClick={() => onNftClick(l.tokenId)}
              className="grid grid-cols-[40px_1fr_110px_100px_90px_70px_90px_80px] gap-4 items-center px-2 py-2 hover:bg-neutral-50 cursor-pointer mobile:grid-cols-[40px_1fr_auto] mobile:gap-3"
            >
              <Thumb src={l.image} size={40} />
              <div className="min-w-0">
                <div className="text-sm font-mono font-medium text-default-font truncate">
                  {l.nft}
                </div>
                <div className="text-[11px] font-mono text-subtext-color truncate">
                  {l.collection}
                </div>
              </div>
              <span className="text-right text-sm font-mono font-semibold text-brand-primary mobile:hidden">
                {l.listPrice !== null ? `${l.listPrice} XRP` : <span className="text-neutral-400">—</span>}
              </span>
              <span className="text-right text-sm font-mono text-neutral-600 mobile:hidden">
                {l.floor} XRP
              </span>
              <span className="text-right text-sm font-mono text-brand-primary mobile:hidden">
                {l.highestOffer ? `${l.highestOffer} XRP` : <span className="text-neutral-400">—</span>}
              </span>
              <span
                className={`text-right text-sm font-mono mobile:hidden ${
                  gap !== null ? gapToneClass(gap) : 'text-neutral-400'
                }`}
              >
                {gap !== null ? `−${gap.toFixed(1)}%` : '—'}
              </span>
              <span className="text-right text-sm font-mono text-neutral-600 mobile:hidden">
                {l.views ?? '—'}
              </span>
              <span
                className={`text-right text-sm font-mono font-medium mobile:hidden ${
                  l.endingIn && l.endingIn.includes('h') ? 'text-warning-800' : 'text-subtext-color'
                }`}
              >
                {l.endingIn || '—'}
              </span>
              {/* Mobile-only summary cell: best offer (headline) + gap. */}
              <div className="hidden mobile:flex mobile:flex-col mobile:items-end mobile:text-right">
                <div className="text-sm font-mono font-semibold text-brand-primary">
                  {l.highestOffer ? `${l.highestOffer} XRP` : '—'}
                </div>
                {l.listPrice !== null && gap !== null ? (
                  <div className={`text-[11px] font-mono ${gapToneClass(gap)}`}>
                    −{gap.toFixed(1)}% vs {l.listPrice}
                  </div>
                ) : (
                  <div className="text-[11px] font-mono text-neutral-400">not listed</div>
                )}
              </div>
            </div>
          );
        })}
      </>
    )}
  </Card>
);

// ─── Since Your Last Visit card ────────────────────────────────
const SinceVisitItem: React.FC<{
  item: DashboardSinceVisitSignal;
  onCollectionClick: (id: string, slug?: string) => void;
  onNftClick: (tokenId: string) => void;
  onViewTab: (tab: 'nfts' | 'offers' | 'history') => void;
}> = ({ item, onCollectionClick, onNftClick, onViewTab }) => {
  let title = '';
  // `detail` can be a string (single-line wrapped) or a React fragment for
  // richer multi-line layouts (used by offer-received).
  let detail: React.ReactNode = '';
  let icon: React.ReactNode = null;
  let toneClass = '';
  let handleClick: () => void = () => {};

  switch (item.kind) {
    case 'new-listings':
      title = `${item.count} new ${item.count === 1 ? 'listing' : 'listings'}`;
      detail = item.detail;
      icon = <SubframeCore.FeatherZap className="w-4 h-4" />;
      toneClass = 'text-brand-800 bg-brand-50 border-brand-200';
      handleClick = () => onCollectionClick(item.collectionId, item.slug || undefined);
      break;
    case 'floor-move':
      title = `Floor ${item.direction === 'up' ? '↑' : '↓'} ${item.change}%`;
      detail = item.detail;
      icon = item.direction === 'up'
        ? <SubframeCore.FeatherTrendingUp className="w-4 h-4" />
        : <SubframeCore.FeatherTrendingDown className="w-4 h-4" />;
      toneClass =
        item.direction === 'up'
          ? 'text-success-800 bg-success-50 border-success-200'
          : 'text-error-800 bg-error-50 border-error-200';
      handleClick = () => onCollectionClick(item.collectionId, item.slug || undefined);
      break;
    case 'offer-received': {
      title = `${item.count} ${item.count === 1 ? 'offer' : 'offers'} received`;
      // Two layouts:
      //  - Single collection: show "<Collection> (floor X XRP)" + "Highest: Y XRP"
      //  - Multiple collections: show "N collections" + "Highest: Y XRP"
      // The specific collection label is dropped in the multi case because
      // the top-offer collection might not match the rest, and anchoring a
      // single name there could mislead.
      const isSingleCollection = item.collectionCount === 1;
      const collectionLine = isSingleCollection
        ? (() => {
            const parts: string[] = [];
            if (item.topCollectionName) parts.push(item.topCollectionName);
            if (item.topFloor > 0) parts.push(`floor ${fmtXRP(item.topFloor)} XRP`);
            if (parts.length === 0) return null;
            const [name, ...rest] = parts;
            return rest.length > 0 ? `${name} (${rest.join(', ')})` : name;
          })()
        : item.collectionCount > 1
          ? `${item.collectionCount} collections`
          : null;
      const topLine =
        item.topOfferPrice > 0 ? `Highest: ${fmtXRP(item.topOfferPrice)} XRP` : null;
      detail = (
        <>
          {collectionLine && <div className="truncate">{collectionLine}</div>}
          {topLine && <div className="truncate">{topLine}</div>}
        </>
      );
      icon = <SubframeCore.FeatherSend className="w-4 h-4" />;
      toneClass = 'text-warning-800 bg-warning-100 border-warning-300';
      handleClick = () => onViewTab('offers');
      break;
    }
    case 'offer-accepted':
      title = item.price > 0
        ? `Sale completed · ${fmtXRP(item.price)} XRP`
        : 'Sale completed';
      detail = item.detail;
      icon = <SubframeCore.FeatherCheck className="w-4 h-4" />;
      toneClass = 'text-success-800 bg-success-50 border-success-200';
      handleClick = () => {
        if (item.tokenId) onNftClick(item.tokenId);
      };
      break;
    case 'market-top-sale':
      title = `Top sale · ${fmtXRP(item.price)} XRP`;
      detail = item.detail;
      icon = <SubframeCore.FeatherAward className="w-4 h-4" />;
      toneClass = 'text-warning-800 bg-warning-50 border-warning-200';
      handleClick = () => {
        if (item.tokenId) onNftClick(item.tokenId);
      };
      break;
    case 'market-trending':
      title = item.detail;
      detail = `${item.salesCount} ${item.salesCount === 1 ? 'sale' : 'sales'} · ${item.buyerCount} ${item.buyerCount === 1 ? 'buyer' : 'buyers'} · ${fmtXRP(item.volume)} XRP total`;
      icon = <SubframeCore.FeatherActivity className="w-4 h-4" />;
      toneClass = 'text-brand-800 bg-brand-50 border-brand-200';
      handleClick = () => onCollectionClick(item.collectionId, item.slug || undefined);
      break;
    case 'market-rising-floor':
      title = `Floor ↑ ${item.change.toFixed(1)}%`;
      detail = `${item.detail}${item.currentFloor > 0 ? ` · floor ${fmtXRP(item.currentFloor)} XRP` : ''}`;
      icon = <SubframeCore.FeatherTrendingUp className="w-4 h-4" />;
      toneClass = 'text-success-800 bg-success-50 border-success-200';
      handleClick = () => onCollectionClick(item.collectionId, item.slug || undefined);
      break;
    case 'market-pulse':
      title = `${item.salesCount.toLocaleString()} ${item.salesCount === 1 ? 'sale' : 'sales'}`;
      detail = `${fmtXRP(item.volume)} XRP traded market-wide`;
      icon = <SubframeCore.FeatherBarChart2 className="w-4 h-4" />;
      toneClass = 'text-neutral-700 bg-neutral-50 border-neutral-border';
      handleClick = () => {};
      break;
  }

  return (
    <div
      onClick={handleClick}
      className={`flex items-center gap-3 p-3 border cursor-pointer hover:opacity-90 transition-opacity ${toneClass}`}
    >
      <div className="w-8 h-8 flex items-center justify-center flex-none">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-mono font-semibold truncate">{title}</div>
        <div className="text-[11px] font-mono text-subtext-color leading-snug">
          {typeof detail === 'string' ? (
            <div className="line-clamp-2">{detail}</div>
          ) : (
            detail
          )}
        </div>
      </div>
    </div>
  );
};

const SinceVisitCard: React.FC<{
  sinceVisit: DashboardSinceVisit;
  onCollectionClick: (id: string, slug?: string) => void;
  onNftClick: (tokenId: string) => void;
  onViewTab: (tab: 'nfts' | 'offers' | 'history') => void;
}> = ({ sinceVisit, onCollectionClick, onNftClick, onViewTab }) => {
  if (sinceVisit.items.length === 0) return null;
  const hasMarketFallback = sinceVisit.items.some(
    i => i.kind === 'market-top-sale' || i.kind === 'market-trending'
  );
  return (
    <Card>
      <SectionLabel>Insights from the last 24h</SectionLabel>
      {hasMarketFallback && (
        <div className="text-[11px] font-mono text-neutral-400 -mt-1">
          Personal signals + recent market activity.
        </div>
      )}
      <div className="grid grid-cols-4 gap-3 mobile:grid-cols-1">
        {sinceVisit.items.map((item, i) => (
          <SinceVisitItem
            key={i}
            item={item}
            onCollectionClick={onCollectionClick}
            onNftClick={onNftClick}
            onViewTab={onViewTab}
          />
        ))}
      </div>
    </Card>
  );
};

// ─── Personal (logged-in) Home ─────────────────────────────────
const PersonalHome: React.FC<{
  dashboard: DashboardData;
  topVolume: LeaderboardCollection[];
  trending: TrendingCollection[];
  topGainers: FloorPriceGainerCollection[];
  featured: any;
  activePromotions: PromotedNft[];
  onCollectionClick: (id: string, slug?: string) => void;
  onNftClick: (tokenId: string) => void;
  onViewTab: (tab: 'nfts' | 'offers' | 'history') => void;
}> = ({
  dashboard,
  topVolume,
  trending,
  topGainers,
  featured,
  activePromotions,
  onCollectionClick,
  onNftClick,
  onViewTab,
}) => {
  // Defensive defaults: if any array field is missing (e.g. a cached
  // response from before a field was added, or a transient partial
  // payload), fall back to empty arrays so downstream `.length` /
  // `.map` / `.slice` calls don't throw.
  const { portfolio } = dashboard;
  const collections = dashboard.collections ?? [];
  const expiringOffers = dashboard.expiringOffers ?? [];
  const buyOffersReceived = dashboard.buyOffersReceived ?? [];
  const topValuedNfts = dashboard.topValuedNfts ?? [];
  const sinceVisit = dashboard.sinceVisit ?? { since: new Date().toISOString(), items: [] };
  const hasExpiring = expiringOffers.length > 0;

  return (
    <div className="w-full flex flex-col gap-6">
      {/* Featured collection pinned to the top */}
      {featured && (
        <FeaturedStrip
          featured={featured}
          onClick={() => onCollectionClick(featured.collectionId, featured.slug)}
          onNftClick={onNftClick}
        />
      )}

      {/* Portfolio summary */}
      <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-0 border border-neutral-border mobile:grid-cols-1">
        <div className="p-5 border-r border-neutral-border flex flex-col gap-2 mobile:p-3 mobile:border-r-0 mobile:border-b">
          <div className="flex items-center gap-2">
            <SubframeCore.FeatherWallet className="w-3.5 h-3.5 text-subtext-color" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-subtext-color">
              Portfolio value
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[30px] leading-9 font-mono font-semibold text-default-font mobile:text-2xl mobile:leading-7">
              {portfolio.value.xrp.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
            <span className="text-sm font-mono text-subtext-color">XRP</span>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono flex-wrap">
            {portfolio.value.usd !== null && (
              <span className="text-subtext-color">≈ {fmtUSD(portfolio.value.usd)}</span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Delta value={portfolio.value.change24h_pct} />
              <span className="text-neutral-400">vs 24h</span>
            </span>
          </div>
        </div>
        <StatCell
          label="Wallet balance"
          value={
            portfolio.balance.xrp !== null
              ? portfolio.balance.xrp.toLocaleString(undefined, { maximumFractionDigits: 0 })
              : '—'
          }
          unit={portfolio.balance.xrp !== null ? 'XRP' : undefined}
          hint={
            portfolio.balance.usd !== null
              ? `≈ ${fmtUSD(portfolio.balance.usd)}`
              : 'visit your wallet to refresh'
          }
        />
        <StatCell
          label="Buy offers"
          value={portfolio.counts.buyOffers}
          hint={
            portfolio.counts.soonestExpiryMins !== null
              ? `soonest expires in ${fmtExpires(portfolio.counts.soonestExpiryMins)}`
              : '0 expiring soon'
          }
          hintWarn={
            portfolio.counts.soonestExpiryMins !== null &&
            portfolio.counts.soonestExpiryMins < 360
          }
        />
        <StatCell
          label="Sell offers"
          value={portfolio.counts.sellOffers}
          hint={`${portfolio.counts.endingSoon} ending soon`}
          hintWarn={portfolio.counts.endingSoon > 0}
        />
      </div>

      {/* Since your last visit */}
      <SinceVisitCard
        sinceVisit={sinceVisit}
        onCollectionClick={onCollectionClick}
        onNftClick={onNftClick}
        onViewTab={onViewTab}
      />

      {/* If the user has outgoing buy offers expiring soon, pin that card
          above the full-width section — time-sensitive > browsable. */}
      {hasExpiring && (
        <ExpiringOffersCard
          offers={expiringOffers}
          totalBuyOffers={portfolio.counts.buyOffers}
          onNftClick={onNftClick}
          onViewAll={() => onViewTab('offers')}
        />
      )}

      {/* Full-width: offers received on the user's NFTs, rich table. */}
      <BuyOffersReceivedCard
        rows={buyOffersReceived}
        onNftClick={onNftClick}
        onViewAll={() => onViewTab('offers')}
      />

      {/* Two-col: Top Valued NFTs + My Collections */}
      <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1">
        <TopValuedNftsCard nfts={topValuedNfts} onNftClick={onNftClick} />
        <MyCollectionsCard
          collections={collections}
          onCollectionClick={onCollectionClick}
          onViewAll={() => onViewTab('nfts')}
        />
      </div>

      {/* Section boundary between personal data and market-wide stats.
          Matches the "My Dashboard" H1 style for visual parallelism. */}
      <div className="mt-2 w-full h-px bg-neutral-border" />
      <h1 className="text-xl font-mono font-semibold text-default-font mobile:text-heading-2 mobile:font-heading-2">
        Market Analytics
      </h1>

      <MarketStats
        topVolume={topVolume}
        trending={trending}
        topGainers={topGainers}
        activePromotions={activePromotions}
        onCollectionClick={onCollectionClick}
        onNftClick={onNftClick}
      />
    </div>
  );
};

// ─── Main page ──────────────────────────────────────────────────
export const DashboardV2Page: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { data: homeData, isLoading: homeLoading, isError: homeError } = useHomePageData();
  const {
    data: dashboard,
    isLoading: dashboardLoading,
    isError: dashboardError,
  } = useDashboard(isAuthenticated);

  const handleCollectionClick = (id: string, slug?: string) => {
    navigate(`/collections/${slug || id}`);
  };
  const handleNftClick = (tokenId: string) => {
    navigate(`/nfts/${tokenId}`);
  };
  const walletUrl = (tab: 'nfts' | 'offers' | 'history' = 'nfts') =>
    user?.xrpl_address ? `/wallets/${user.xrpl_address}?tab=${tab}` : '/';

  const isLoading = homeLoading || (isAuthenticated && dashboardLoading);
  const isError = homeError || (isAuthenticated && dashboardError);

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center px-6 py-24 gap-4">
          <LoadingSpinner size="lg" className="text-brand-primary" />
        </div>
      </DefaultPageLayout>
    );
  }

  if (isError || !homeData) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center px-6 py-12">
          <div className="text-heading-2 font-heading-2 text-error-600 mb-4">Error loading data</div>
          <div className="text-body font-body text-subtext-color">Please try refreshing the page</div>
        </div>
      </DefaultPageLayout>
    );
  }

  const { featuredCollection, topVolume, trending, topGainers, activePromotions } = homeData;

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 mobile:gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-mono font-semibold text-default-font mobile:text-heading-2 mobile:font-heading-2">
            {isAuthenticated ? 'My Dashboard' : 'XRP Deals'}
          </h1>
          {isAuthenticated && user?.xrpl_address && <AddressWithCopy address={user.xrpl_address} />}
        </div>

        {isAuthenticated && dashboard ? (
          <PersonalHome
            dashboard={dashboard}
            topVolume={topVolume}
            trending={trending}
            topGainers={topGainers}
            featured={featuredCollection}
            activePromotions={activePromotions}
            onCollectionClick={handleCollectionClick}
            onNftClick={handleNftClick}
            onViewTab={tab => navigate(walletUrl(tab))}
          />
        ) : (
          <MarketHome
            topVolume={topVolume}
            trending={trending}
            topGainers={topGainers}
            featured={featuredCollection}
            activePromotions={activePromotions}
            onCollectionClick={handleCollectionClick}
            onNftClick={handleNftClick}
          />
        )}
      </div>
    </DefaultPageLayout>
  );
};
